import { useState, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ConversionPerformance } from '@/components/dashboard/ConversionPerformance';
import { TemperatureDistributionChart } from '@/components/dashboard/TemperatureDistributionChart';
import { AtRiskDrawer } from '@/components/dashboard/AtRiskDrawer';
import { QuickAddDealModal } from '@/components/deals/QuickAddDealModal';
import { useDashboardStats } from '@/hooks/useActionEvents';
import { useUnifiedMetrics, formatCurrency } from '@/hooks/useUnifiedMetrics';
import { LoadingState } from '@/components/LoadingState';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  BarChart3,
  Zap,
  AlertTriangle,
  Flame,
  TrendingUp,
  DollarSign,
  Target,
  Clock,
  Award,
  Users,
  ArrowUpRight,
  ArrowDownRight,
  Info,
  Plus,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';
import { format, subDays } from 'date-fns';
import { cn } from '@/lib/utils';
import Insights from './Insights';
import Results from './Results';

const TEMPERATURE_COLORS = {
  hot: 'hsl(0, 84%, 60%)',
  warm: 'hsl(38, 92%, 50%)',
  cold: 'hsl(210, 100%, 50%)',
};

const SOURCE_COLORS = [
  'hsl(224, 76%, 48%)',
  'hsl(142, 71%, 45%)',
  'hsl(38, 92%, 50%)',
  'hsl(262, 83%, 58%)',
  'hsl(199, 89%, 48%)',
  'hsl(0, 72%, 51%)',
  'hsl(220, 9%, 46%)',
];

function KPICard({ 
  title, 
  value, 
  subtitle, 
  icon: Icon, 
  trend, 
  trendValue,
  iconBg = 'bg-primary/10',
  iconColor = 'text-primary',
  tooltip,
  href,
  clickHint,
  warning,
}: { 
  title: string; 
  value: string | number; 
  subtitle?: string;
  icon: any; 
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  iconBg?: string;
  iconColor?: string;
  tooltip?: string;
  href?: string;
  clickHint?: string;
  warning?: string;
}) {
  const content = (
    <Card className={cn(
      "kpi-card group h-full",
      href && "kpi-card-clickable cursor-pointer"
    )}>
      <CardContent className="pt-5 pb-4 h-full flex flex-col justify-center">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200", 
              iconBg, 
              "group-hover:scale-105"
            )}>
              <Icon className={cn("w-5 h-5 transition-colors", iconColor)} />
            </div>
            <div>
              <p className="text-2xl font-bold kpi-value transition-colors tabular-nums">{value}</p>
              <p className="text-xs text-muted-foreground">{title}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {warning && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertTriangle className="w-3.5 h-3.5 text-warning" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[200px] text-xs">
                  <p>{warning}</p>
                </TooltipContent>
              </Tooltip>
            )}
            {tooltip && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3.5 h-3.5 text-muted-foreground/50 hover:text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[200px] text-xs">
                  <p>{tooltip}</p>
                  {clickHint && href && (
                    <p className="mt-1 text-primary/80 font-medium">Click to {clickHint}</p>
                  )}
                </TooltipContent>
              </Tooltip>
            )}
            {trend && trendValue && (
              <div className={cn(
                "flex items-center gap-1 text-xs",
                trend === 'up' ? 'text-success' : trend === 'down' ? 'text-destructive' : 'text-muted-foreground'
              )}>
                {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : trend === 'down' ? <ArrowDownRight className="w-3 h-3" /> : null}
                {trendValue}
              </div>
            )}
            {href && (
              <ArrowUpRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-primary transition-colors opacity-0 group-hover:opacity-100" />
            )}
          </div>
        </div>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-2">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );

  if (href) {
    return <Link to={href} className="block h-full">{content}</Link>;
  }
  return content;
}

function DashboardOverview() {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<7 | 30>(7);
  const [showConversionSection, setShowConversionSection] = useState(false);
  const [atRiskOpen, setAtRiskOpen] = useState(false);
  const [quickAddDealOpen, setQuickAddDealOpen] = useState(false);
  const conversionRef = useRef<HTMLDivElement>(null);
  const { data: stats, isLoading: statsLoading, error: statsError } = useDashboardStats();
  const { metrics, isLoading: metricsLoading } = useUnifiedMetrics();

  const isLoading = statsLoading || metricsLoading;

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <LoadingState message="Loading dashboard..." />
      </div>
    );
  }

  if (statsError) {
    return (
      <div className="container mx-auto px-4 py-8">
        <EmptyState
          icon={BarChart3}
          title="Something went wrong"
          description="We couldn't load your dashboard. Please try again."
          action={
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          }
        />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="container mx-auto px-4 py-8">
        <EmptyState
          icon={BarChart3}
          title="No data yet"
          description="Start taking actions on leads to see your analytics."
          action={
            <Button asChild>
              <Link to="/app/today">Go to Today</Link>
            </Button>
          }
        />
      </div>
    );
  }

  // Use unified metrics for correct calculations
  const winRate = timeRange === 7 ? metrics?.winRate7d : metrics?.winRate30d;
  const wonRevenue = timeRange === 7 ? metrics?.wonRevenue7d : metrics?.wonRevenue30d;
  const wonCount = timeRange === 7 ? metrics?.wonCount7d : metrics?.wonCount30d;
  const lostCount = timeRange === 7 ? metrics?.lostCount7d : metrics?.lostCount30d;

  // Prepare chart data
  const actionsDaily = timeRange === 7 ? stats.actionsDaily7 : stats.actionsDaily30;
  const chartData = [];
  for (let i = timeRange - 1; i >= 0; i--) {
    const date = format(subDays(new Date(), i), 'yyyy-MM-dd');
    const displayDate = format(subDays(new Date(), i), 'MMM d');
    chartData.push({
      date: displayDate,
      actions: actionsDaily[date] || 0,
    });
  }

  const temperatureData = [
    { name: 'Hot', value: metrics?.hotLeads || 0, color: TEMPERATURE_COLORS.hot },
    { name: 'Warm', value: metrics?.warmLeads || 0, color: TEMPERATURE_COLORS.warm },
    { name: 'Cold', value: metrics?.coldLeads || 0, color: TEMPERATURE_COLORS.cold },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Top KPI Row - Health Check (ONLY 4 metrics) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-fr">
        <KPICard
          title="Open Pipeline"
          value={formatCurrency(metrics?.pipelineValue || 0)}
          icon={DollarSign}
          iconBg="bg-primary/10"
          iconColor="text-primary"
          tooltip="Total value of deals not yet won or lost. Focus on moving these forward."
          href="/app/pipeline"
          clickHint="view pipeline"
        />
        <div 
          onClick={() => {
            setShowConversionSection(true);
            setTimeout(() => {
              conversionRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
          }}
          className="block h-full cursor-pointer"
        >
          <Card className="kpi-card kpi-card-clickable group h-full">
            <CardContent className="pt-5 pb-4 h-full flex flex-col justify-center">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200 bg-success/10 group-hover:scale-105">
                    <Target className="w-5 h-5 transition-colors text-success" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold kpi-value transition-colors tabular-nums">
                      {(winRate || 0).toFixed(0)}%
                    </p>
                    <p className="text-xs text-muted-foreground">Win Rate</p>
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-primary transition-colors opacity-0 group-hover:opacity-100" />
              </div>
            </CardContent>
          </Card>
        </div>
        <div 
          onClick={() => navigate('/app/today')}
          className="block h-full cursor-pointer"
        >
          <Card className={cn(
            "kpi-card kpi-card-clickable group h-full",
            (metrics?.needAction || 0) > 0 && "border-destructive/20"
          )}>
            <CardContent className="pt-5 pb-4 h-full flex flex-col justify-center">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200 group-hover:scale-105",
                    (metrics?.needAction || 0) > 0 ? "bg-destructive/10" : "bg-muted"
                  )}>
                    <AlertTriangle className={cn(
                      "w-5 h-5 transition-colors",
                      (metrics?.needAction || 0) > 0 ? "text-destructive" : "text-muted-foreground"
                    )} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold kpi-value transition-colors tabular-nums">
                      {metrics?.needAction || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Need Action</p>
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-primary transition-colors opacity-0 group-hover:opacity-100" />
              </div>
            </CardContent>
          </Card>
        </div>
        <KPICard
          title="Won Revenue"
          value={formatCurrency(wonRevenue || 0)}
          icon={Award}
          iconBg="bg-success/10"
          iconColor="text-success"
          tooltip={`Total revenue from ${wonCount} closed-won deals in ${timeRange}d.`}
          href="/app/pipeline"
          clickHint="view won deals"
        />
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Actions Over Time */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base font-medium">Actions Completed</CardTitle>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3.5 h-3.5 text-muted-foreground/50 hover:text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[200px] text-xs">
                  Calls, messages, and follow-ups completed over time.
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="flex gap-1 bg-secondary rounded-lg p-1">
              <Button
                variant={timeRange === 7 ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setTimeRange(7)}
                className="text-xs h-7"
              >
                7d
              </Button>
              <Button
                variant={timeRange === 30 ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setTimeRange(30)}
                className="text-xs h-7"
              >
                30d
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {chartData.some(d => d.actions > 0) ? (
              <div className="chart-animate-in">
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorActions" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      className="text-muted-foreground"
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      className="text-muted-foreground"
                      allowDecimals={false}
                      width={30}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="actions"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorActions)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[220px] flex flex-col items-center justify-center text-muted-foreground text-sm gap-2">
                <p>No actions recorded yet</p>
                <Button size="sm" variant="outline" asChild>
                  <Link to="/app/today">Complete 1 action</Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Temperature Distribution - with time range */}
        <TemperatureDistributionChart timeRange={timeRange} />
      </div>

      {/* Metrics Summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Revenue Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-secondary/50">
              <p className="text-xs text-muted-foreground mb-1">Pipeline Value</p>
              <p className="text-xl font-bold tabular-nums">{formatCurrency(metrics?.pipelineValue || 0)}</p>
              <p className="text-xs text-muted-foreground mt-1">{metrics?.openDealsCount || 0} open deals</p>
            </div>
            <div className="p-4 rounded-lg bg-secondary/50">
              <p className="text-xs text-muted-foreground mb-1">Forecast</p>
              <p className="text-xl font-bold tabular-nums">{formatCurrency(metrics?.weightedForecast || 0)}</p>
              <p className="text-xs text-muted-foreground mt-1">Weighted by probability</p>
            </div>
            <div className="p-4 rounded-lg bg-success/5 border border-success/20">
              <p className="text-xs text-muted-foreground mb-1">Won Revenue (All Time)</p>
              <p className="text-xl font-bold tabular-nums text-success">{formatCurrency(metrics?.wonRevenue || 0)}</p>
              <p className="text-xs text-muted-foreground mt-1">{metrics?.wonCount || 0} deals won</p>
            </div>
            <div className="p-4 rounded-lg bg-secondary/50">
              <p className="text-xs text-muted-foreground mb-1">Lead→Won Rate</p>
              <p className="text-xl font-bold tabular-nums">{(metrics?.leadToWonRate || 0).toFixed(1)}%</p>
              <p className="text-xs text-muted-foreground mt-1">
                {metrics?.wonCount || 0} won / {metrics?.totalLeads || 0} total
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Conversion Performance Section */}
      {showConversionSection && (
        <div ref={conversionRef}>
          <ConversionPerformance />
        </div>
      )}

      {/* Modals */}
      <AtRiskDrawer open={atRiskOpen} onOpenChange={setAtRiskOpen} />
      <QuickAddDealModal open={quickAddDealOpen} onOpenChange={setQuickAddDealOpen} />
    </div>
  );
}

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState<'overview' | 'insights' | 'results'>('overview');

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
            <p className="text-muted-foreground text-sm">
              Revenue &amp; execution health
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="default" asChild className="btn-premium">
            <Link to="/app/today" className="flex items-center gap-1.5">
              <Zap className="w-4 h-4" />
              Go to Today
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="insights">Insights</TabsTrigger>
          <TabsTrigger value="results">Results</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-0">
          <DashboardOverview />
        </TabsContent>

        <TabsContent value="insights" className="mt-0">
          <Insights />
        </TabsContent>

        <TabsContent value="results" className="mt-0">
          <Results />
        </TabsContent>
      </Tabs>
    </div>
  );
}
